import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { isValid, parse } from "date-fns";
import uuid from "uuid";

import { formatDate, formatDateAt } from "simplydo/core/dates";
import { removeLocalStorageItem } from "simplydo/core/helpers";
import type { OpenAPI } from "simplydo/interfaces";
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Grid,
  Icon,
  Message,
  Modal,
  Popup,
  Segment,
  Select,
} from "simplydo/react/ui";

import api from "api";
import util from "utils/utils";

import {
  type ConditionCheckFieldType,
  availableConditions,
  conditionChecks,
  getDefaultCondition,
  renderValueCheck,
} from "components/ideas/Body/Custom/fieldCondition";
import { DateInput } from "components/lib/DateInputs";
import { RadioHeader, TooltipButton } from "components/lib/UI";

import AnnouncementBase from "./Base";
import type {
  AnnouncementConditionConfig,
  AnnouncementIdeaFieldCondition,
  AnnouncementListSetState,
  AnnouncementRecipientCondition,
  AnnouncementSendWhenCondition,
  AnnouncementSetState,
  AnnouncementSubconditionOptions,
  EditableAnnouncement,
} from "./types";
import {
  EMPTY_ANNOUNCEMENT,
  dropdownStringValue,
  isAnnouncementClosed,
  isSendAtValid,
} from "./types";

type IdeaTemplateField = NonNullable<
  OpenAPI.Schemas["IdeaTemplate"]["body"]
>[number]["fields"][number];

type AnnouncementModalProps = {
  forType: string;
  forId: string;
  standardConditions: AnnouncementConditionConfig[];
  automaticConditions?: AnnouncementConditionConfig[];
  standardConditionsRequired: boolean;
  scheduledAnnouncements: EditableAnnouncement[];
  announcement: EditableAnnouncement;
  deleteAnnouncement: (id: string, callback: () => void) => void;
  setAnnouncement: AnnouncementSetState;
  setScheduledAnnouncements: AnnouncementListSetState;
  automaticAnnouncements: EditableAnnouncement[];
  setAutomaticAnnouncements: AnnouncementListSetState;
  copyToNew?: (announcement: EditableAnnouncement) => void;
  enabledAnnouncementTypes?: string[];
  setEmailPreviewOpen: (open: boolean) => void;
};

const AnnouncementModal = ({
  forType,
  forId,
  standardConditions,
  standardConditionsRequired,
  scheduledAnnouncements,
  setScheduledAnnouncements,
  announcement,
  setAnnouncement,
  deleteAnnouncement,
  automaticConditions,
  setAutomaticAnnouncements,
  automaticAnnouncements,
  copyToNew,
  setEmailPreviewOpen,
  enabledAnnouncementTypes = ["standard", "automatic"],
}: AnnouncementModalProps) => {
  const [recipientsMatched, setRecipientsMatched] = useState(0);
  const [subconditionOptions, setSubconditionOptions] =
    useState<AnnouncementSubconditionOptions>({});
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  const addCondition = useCallback(() => {
    setAnnouncement((prevAnn) => ({
      ...prevAnn,
      recipientConditions: [
        ...(prevAnn.recipientConditions ?? []),
        { type: "", data: "", key: uuid.v4() },
      ],
    }));
  }, [setAnnouncement]);

  const getStandardSubconditions = useCallback(
    (standardCondition: AnnouncementRecipientCondition | undefined) =>
      standardConditions.find((sc) => sc.value === standardCondition?.type)
        ?.subConditions,
    [standardConditions],
  );
  const getAutomaticSubconditions = useCallback(
    (condition: string | undefined) =>
      automaticConditions?.find((sc) => sc.value === condition)?.subConditions,
    [automaticConditions],
  );
  const automaticSubconditions = getAutomaticSubconditions(
    announcement.sendWhen,
  );

  const updateCondition = useCallback(
    (
      conditionIndex: number,
      label: keyof AnnouncementRecipientCondition,
      value: AnnouncementRecipientCondition[keyof AnnouncementRecipientCondition],
    ) => {
      const updatedRecipientConditions = [
        ...(announcement.recipientConditions ?? []),
      ];
      const condition = updatedRecipientConditions[conditionIndex];
      if (!condition) return;
      updatedRecipientConditions[conditionIndex] = {
        ...condition,
        [label]: value,
      };
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const deleteCondition = useCallback(
    (conditionIndex: number) => {
      const updatedRecipientConditions = [
        ...(announcement.recipientConditions ?? []),
      ];
      updatedRecipientConditions.splice(conditionIndex, 1);
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  const updateSubconditionOptions = useCallback(
    (
      conditionKey: string | undefined,
      option: string | string[],
      selectSingle: boolean,
    ) => {
      if (!conditionKey) return;
      const updatedRecipientConditions = (
        announcement.recipientConditions ?? []
      ).map((condition) => {
        if (condition.key !== conditionKey) {
          return condition;
        }
        if (selectSingle) {
          return { ...condition, data: option };
        }

        const options = Array.isArray(option) ? option : [option];
        return { ...condition, data: options };
      });
      setAnnouncement((prev) => ({
        ...prev,
        recipientConditions: updatedRecipientConditions,
      }));
    },
    [announcement.recipientConditions, setAnnouncement],
  );

  useEffect(() => {
    let fetching = true;
    api.announcements.getAudience(
      forType,
      forId,
      {
        type: "standard",
        recipientConditions: announcement.recipientConditions,
      },
      ({ recipientsMatched: response }) => {
        if (!fetching) return;
        setRecipientsMatched(response);
      },
      (err) => {
        if (!fetching) return;
        toast.error(err.message);
        setRecipientsMatched(0);
      },
    );

    return () => {
      fetching = false;
    };
  }, [forType, forId, announcement.recipientConditions]);

  const createAnnouncement = useCallback(() => {
    if (announcement.replyTo && !util.validateEmail(announcement.replyTo)) {
      toast.error("Please enter a valid reply-to email address");
      return;
    }

    setSending(true);

    const resolvedType =
      announcement.type === "standard" && announcement.sendAt
        ? "scheduled"
        : announcement.type;

    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const type = resolvedType;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const scheduledFields = { recipientConditions, sendAt };

    const standardFields = { recipientConditions };
    api.announcements.create(
      forType,
      forId,
      {
        type,
        subject,
        body,
        text,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (createdAnnouncement) => {
        const savedAnnouncement = createdAnnouncement as EditableAnnouncement;
        toast.success(
          savedAnnouncement.type === "standard"
            ? t("announcements.sent", {
                audience: `${savedAnnouncement.recipientsMatched ?? 0} recipients`,
              })
            : t("announcements.created"),
        );
        setSending(false);
        setAnnouncement(EMPTY_ANNOUNCEMENT);
        setRecipientsMatched(0);
        removeLocalStorageItem(`announcement-${forId}`);
        if (savedAnnouncement.type === "automatic") {
          setAutomaticAnnouncements((prev) => [...prev, savedAnnouncement]);
        } else if (savedAnnouncement.type === "scheduled") {
          setScheduledAnnouncements((prev) => [...prev, savedAnnouncement]);
        }
        setTimeout(() => {
          api.invalidateQueries(api.announcements.getLogs, [forType, forId]);
        }, 1000);
      },
      (err) => {
        setSending(false);
        toast.error(err.message);
      },
    );
  }, [
    forType,
    forId,
    announcement,
    setAnnouncement,
    setScheduledAnnouncements,
    setAutomaticAnnouncements,
    t,
  ]);

  const updateAnnouncement = useCallback(() => {
    if (announcement.replyTo && !util.validateEmail(announcement.replyTo)) {
      toast.error("Please enter a valid reply-to email address");
      return;
    }

    setSending(true);
    const {
      subject,
      body,
      text,
      replyTo,
      callToActionText,
      callToActionLink,
      recipientConditions,
      calendarEvent,
      attachments,
      type,
      sendAt,
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
      teamsMeeting,
    } = announcement;

    const automaticFields = {
      sendWhen,
      sendWhenConditions,
      fullScreen,
      toUserCondition,
      toUsers,
    };
    const standardFields = { recipientConditions };
    const scheduledFields = { recipientConditions, sendAt };
    api.announcements.update(
      forType,
      forId,
      announcement._id ?? "",
      {
        type,
        subject,
        body,
        text,
        sendWhen,
        replyTo,
        callToActionText,
        callToActionLink,
        calendarEvent,
        attachments,
        teamsMeeting,
        ...(type === "automatic" ? automaticFields : {}),
        ...(type === "scheduled" ? scheduledFields : {}),
        ...(type === "standard" ? standardFields : {}),
      },
      (updatedAnnouncement) => {
        const savedAnnouncement = updatedAnnouncement as EditableAnnouncement;
        if (savedAnnouncement.type === "automatic") {
          setAutomaticAnnouncements(
            automaticAnnouncements.map((a) =>
              a._id === savedAnnouncement._id ? savedAnnouncement : a,
            ),
          );
        } else {
          setScheduledAnnouncements(
            scheduledAnnouncements.map((a) =>
              a._id === savedAnnouncement._id ? savedAnnouncement : a,
            ),
          );
        }
        // Need to invalidate board lane query to reflect changes to subject in the move-to-lane announcement type
        if (forType === "challenge" && announcement.type === "automatic") {
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
        }
        setSending(false);
        setAnnouncement(EMPTY_ANNOUNCEMENT);
        removeLocalStorageItem(`announcement-${forId}`);
        toast.success(t("generic.typeSaved", { type: "Announcement" }));
      },
      (err) => {
        toast.error(err.message);
        setSending(false);
      },
    );
  }, [
    t,
    forType,
    forId,
    announcement,
    scheduledAnnouncements,
    setScheduledAnnouncements,
    setAnnouncement,
    automaticAnnouncements,
    setAutomaticAnnouncements,
  ]);

  const { recipientConditions = [] } = announcement;

  useEffect(() => {
    if (announcement.type === "automatic") {
      const cond = (automaticConditions ?? []).find(
        (sc) => sc.value === announcement.sendWhen,
      );
      if (cond?.subConditions) {
        cond.subConditions.forEach((subcondition) => {
          if (
            subcondition.getOptions &&
            subcondition.key &&
            !subconditionOptions[subcondition.key]
          ) {
            subcondition.getOptions(forId, (options) =>
              setSubconditionOptions((prev) => ({
                ...prev,
                [subcondition.key]: options,
              })),
            );
          }
        });
      }
    } else {
      recipientConditions.forEach((c) => {
        const cond = standardConditions.find((sc) => sc.value === c.type);
        if (cond?.subConditions) {
          cond.subConditions.forEach((subcondition) => {
            if (
              subcondition.getOptions &&
              subcondition.key &&
              !subconditionOptions[subcondition.key]
            ) {
              subcondition.getOptions(forId, (options) =>
                setSubconditionOptions((prev) => ({
                  ...prev,
                  [subcondition.key]: options,
                })),
              );
            }
          });
        }
      });
    }
  }, [
    standardConditions,
    subconditionOptions,
    forId,
    recipientConditions,
    announcement.type,
    announcement.sendWhen,
    automaticConditions,
  ]);

  const [sendDisabled, sendDisabledReason] = useMemo(() => {
    const hasSendAt =
      announcement.sendAt !== undefined &&
      announcement.sendAt !== null &&
      announcement.sendAt !== "";

    if (util.isEmpty(announcement.subject))
      return [true, "Please fill in the announcement subject and body."];
    if (util.isEmpty(announcement.body))
      return [true, "Please fill in the announcement subject and body."];
    if (announcement.type === "automatic") {
      if (!announcement.sendWhen) {
        return [true, "At least one automation rule is required."];
      }
    } else {
      if (
        standardConditionsRequired &&
        standardConditions?.length &&
        recipientConditions.length === 0
      ) {
        return [true, "Announcements must be sent to at least one user."];
      }
      if (
        (announcement.type === "scheduled" || hasSendAt) &&
        announcement.sendAt &&
        !isValid(new Date(announcement.sendAt))
      ) {
        return [true, "Please select a valid send date and time."];
      }
    }
    if (announcement.calendarEvent) {
      if (
        announcement.calendarEvent.startDate &&
        !isValid(new Date(announcement.calendarEvent.startDate))
      ) {
        return [
          true,
          "Please select a valid calendar event start date and time.",
        ];
      }
      if (
        announcement.calendarEvent.endDate &&
        !isValid(new Date(announcement.calendarEvent.endDate))
      ) {
        return [
          true,
          "Please select a valid calendar event end date and time.",
        ];
      }
      if (!announcement.calendarEvent.organiserName) {
        return [true, "Please enter a calendar event organiser name."];
      }
      if (!announcement.calendarEvent.organiserEmail) {
        return [true, "Please enter a calendar event organiser email."];
      }
      if (!announcement.calendarEvent.name) {
        return [true, "Please enter a calendar event name."];
      }
    }

    return [false, null];
  }, [
    announcement,
    standardConditions,
    recipientConditions,
    standardConditionsRequired,
  ]);

  const sendAtValid = useMemo(
    () => isSendAtValid(announcement.sendAt),
    [announcement.sendAt],
  );

  const mapSubconditionOptions = (
    options: AnnouncementSubconditionOptions[string] | undefined,
  ) =>
    (options ?? []).map((s) => ({
      key: s._id ?? s.value ?? s.key ?? "",
      value: s._id ?? s.value ?? s.key ?? "",
      text: s.name ?? s.text ?? s.title ?? "",
    }));
  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={!isAnnouncementClosed(announcement)}
      onClose={() => setAnnouncement(EMPTY_ANNOUNCEMENT)}
    >
      <Modal.Header>
        {announcement._id ? (
          t("announcements.automatic.edit.title")
        ) : (
          <>
            {localStorage.getItem(`announcement-${forId}`)
              ? t("announcements.manual.continue.title")
              : t("announcements.automatic.new.title")}
            <br />
            <small>{t("announcements.manual.sendInfo")}</small>
          </>
        )}
      </Modal.Header>
      <Modal.Content scrolling>
        <Segment>
          <h3>Announcement type</h3>
          {(announcement._id &&
            (announcement.type === "standard" ||
              announcement.type === "scheduled")) ||
          !announcement._id ? (
            <>
              <RadioHeader
                header="Standard"
                as="h4"
                content="Send or schedule announcements to a custom audience"
                value="standard"
                checked={
                  announcement.type === "standard" ||
                  announcement.type === "scheduled"
                }
                onChange={() =>
                  setAnnouncement((prev) => ({ ...prev, type: "standard" }))
                }
              />
              {(announcement.type === "standard" ||
                announcement.type === "scheduled") && (
                <div
                  style={{
                    marginLeft: 30,
                    marginBottom: 20,
                    borderLeft: "1px solid #d3cfcf",
                    paddingLeft: 20,
                  }}
                >
                  {standardConditions?.length ? (
                    <>
                      <h5>{t("announcements.manual.recipients.title")}</h5>
                      <p>{t("announcements.manual.recipients.info")}</p>
                      {standardConditionsRequired ? null : (
                        <p>
                          {t(
                            "announcements.manual.recipients.condition.optional",
                            { type: forType },
                          )}
                        </p>
                      )}
                      <h5 style={{ fontSize: "small", marginBottom: 5 }}>
                        Recipient conditions{" "}
                        {standardConditionsRequired ? (
                          <Icon
                            name="asterisk"
                            style={{
                              position: "relative",
                              top: -5,
                              marginLeft: 3,
                            }}
                            color="red"
                            size="tiny"
                          />
                        ) : null}
                      </h5>
                      {recipientConditions.map((c, i) => {
                        const subconditions = getStandardSubconditions(c);

                        return (
                          <>
                            <Grid stackable key={c.key}>
                              <Grid.Column computer={8}>
                                <Select
                                  placeholder={t(
                                    "announcements.manual.recipients.condition.select",
                                  )}
                                  fluid
                                  options={standardConditions.map((s) => ({
                                    ...s,
                                    key: s.value,
                                    text: t(s.text),
                                  }))}
                                  onChange={(_e, s) => {
                                    updateCondition(
                                      i,
                                      "type",
                                      dropdownStringValue(s.value) ?? null,
                                    );
                                  }}
                                  value={c.type ?? undefined}
                                />
                              </Grid.Column>
                              <Grid.Column computer={6}>
                                {subconditions?.map((subcondition) => {
                                  return (
                                    <>
                                      {subcondition.type === "dropdown" ? (
                                        <Select
                                          multiple={
                                            subcondition.multiple ?? false
                                          }
                                          placeholder={
                                            subcondition.placeholder ??
                                            "Select an option"
                                          }
                                          fluid
                                          options={(
                                            subconditionOptions[
                                              subcondition.key
                                            ] ?? []
                                          ).map((s) => ({
                                            key: s._id,
                                            value: s._id,
                                            text: s.name,
                                          }))}
                                          onChange={(_e, s) => {
                                            updateSubconditionOptions(
                                              c.key ?? "",
                                              s.value as string | string[],
                                              !subcondition.multiple,
                                            );
                                          }}
                                          value={
                                            c.data === null ? undefined : c.data
                                          }
                                        />
                                      ) : null}
                                      {subcondition.type === "date"
                                        ? (() => {
                                            const dateValue =
                                              typeof c.data === "string"
                                                ? c.data
                                                : "";
                                            return (
                                              <DateInput
                                                placeholder="Date"
                                                value={
                                                  dateValue
                                                    ? formatDate(
                                                        dateValue,
                                                        "yyyy-MM-dd",
                                                      )
                                                    : ""
                                                }
                                                dateFormat="yyyy-MM-dd"
                                                iconPosition="left"
                                                closable
                                                onChange={(_e, { value }) => {
                                                  const parsed = parse(
                                                    value as string,
                                                    "yyyy-MM-dd",
                                                    new Date(),
                                                  );
                                                  updateSubconditionOptions(
                                                    c.key ?? "",
                                                    isValid(parsed)
                                                      ? parsed.toISOString()
                                                      : "",
                                                    true,
                                                  );
                                                }}
                                              />
                                            );
                                          })()
                                        : null}
                                    </>
                                  );
                                })}
                              </Grid.Column>
                              <Grid.Column
                                computer={2}
                                textAlign="center"
                                style={{ marginTop: 5 }}
                              >
                                <Button
                                  size="mini"
                                  basic
                                  icon="trash"
                                  onClick={() => deleteCondition(i)}
                                />
                              </Grid.Column>
                            </Grid>
                            {i !== recipientConditions.length - 1 ? (
                              <Divider horizontal>And</Divider>
                            ) : null}
                          </>
                        );
                      })}
                      <Button
                        size="small"
                        icon="plus"
                        content={t(
                          "announcements.manual.recipients.condition.add",
                        )}
                        basic
                        onClick={addCondition}
                        style={{ marginTop: 10 }}
                      />
                      {recipientsMatched === 0 ? (
                        <h4 style={{ marginTop: 20 }}>
                          <Icon name="exclamation triangle" />{" "}
                          {t("announcements.manual.recipients.none")}
                        </h4>
                      ) : (
                        <h4 style={{ marginTop: 20 }}>
                          This announcement will be sent to {recipientsMatched}{" "}
                          users.
                        </h4>
                      )}
                    </>
                  ) : (
                    <h3>{t("announcements.manual.recipients.all")}</h3>
                  )}
                </div>
              )}
            </>
          ) : null}
          {enabledAnnouncementTypes.includes("automatic") &&
            (announcement.type === "automatic" || !announcement._id) && (
              <div>
                <RadioHeader
                  header="Automatic"
                  content="Configure announcements to be sent automatically based on user actions"
                  value="automatic"
                  as="h4"
                  checked={announcement.type === "automatic"}
                  onChange={() =>
                    setAnnouncement((prev) => ({ ...prev, type: "automatic" }))
                  }
                />
                {announcement.type === "automatic" && (
                  <div
                    style={{
                      marginLeft: 30,
                      borderLeft: "1px solid #d3cfcf",
                      paddingLeft: 20,
                    }}
                  >
                    <h5>Automation rule</h5>
                    Please choose which condition must be met to send this
                    announcement.
                    <Divider hidden />
                    <Form>
                      <Form.Select
                        style={{ display: "block" }}
                        label={t("announcements.automatic.list.sendWhen")}
                        placeholder="Select an event trigger"
                        required
                        options={(automaticConditions ?? []).map((c) => ({
                          key: c.value,
                          text: t(c.text),
                          value: c.value,
                        }))}
                        value={announcement.sendWhen}
                        onChange={(_e, { value }) => {
                          setAnnouncement((prevState) => ({
                            ...prevState,
                            sendWhen: dropdownStringValue(value),
                          }));
                        }}
                      />
                      {announcement.sendWhen === "submitIdeaWithConditions" ? (
                        <>
                          <p>
                            Only send this announcement if{" "}
                            <strong>
                              <Dropdown
                                inline
                                value={
                                  announcement.sendWhenConditions
                                    ?.conditionType ?? "any"
                                }
                                options={[
                                  { key: "any", text: "any of", value: "any" },
                                  { key: "all", text: "all of", value: "all" },
                                ]}
                                onChange={(_e, { value }) => {
                                  const conditionType =
                                    dropdownStringValue(value);
                                  setAnnouncement((prevState) => ({
                                    ...prevState,
                                    sendWhenConditions: {
                                      ...(prevState.sendWhenConditions || {}),
                                      conditionType:
                                        conditionType === "all" ? "all" : "any",
                                    },
                                  }));
                                }}
                              />
                            </strong>{" "}
                            the following conditions are met:
                          </p>
                          {announcement.sendWhenConditions?.conditions?.map(
                            (condition, i) => {
                              const ideaCondition =
                                condition as AnnouncementIdeaFieldCondition;
                              const chosenFieldType =
                                subconditionOptions.ideaFields?.find(
                                  (f) => f.id === ideaCondition.field,
                                )?.type;
                              const isKnownFieldType = (
                                fieldType: string | undefined,
                              ): fieldType is ConditionCheckFieldType =>
                                !!fieldType && fieldType in conditionChecks;
                              const selectedIdeaField =
                                (subconditionOptions.ideaFields?.find(
                                  (chosenField) =>
                                    chosenField.id === ideaCondition.field,
                                ) as IdeaTemplateField | undefined) ??
                                ({
                                  id: ideaCondition.field ?? "",
                                  type: "shorttext",
                                } as IdeaTemplateField);

                              return (
                                <>
                                  <Form.Group
                                    widths="equal"
                                    key={`${i}-${ideaCondition.field ?? ""}-${ideaCondition.condition ?? ""}`}
                                  >
                                    <Form.Field>
                                      {i === 0 ? (
                                        <label htmlFor={`condition-${i}`}>
                                          1. Field with name
                                          {ideaCondition.field &&
                                            !subconditionOptions.ideaFields
                                              ?.filter(
                                                (f) =>
                                                  !!f.type &&
                                                  f.type in conditionChecks,
                                              )
                                              ?.map((f) => f.id)
                                              .includes(
                                                ideaCondition.field ?? "",
                                              ) && (
                                              <Popup
                                                trigger={
                                                  <Icon
                                                    name="warning circle"
                                                    color="red"
                                                    style={{ marginLeft: 5 }}
                                                  />
                                                }
                                                content={`This announcement condition is invalid because the specified field is not present in the ${t("generic.challenge")} ${t("generic.idea")} format.`}
                                              />
                                            )}
                                        </label>
                                      ) : null}
                                      <Form.Dropdown
                                        placholder={
                                          automaticSubconditions?.[0]
                                            ?.placeholder
                                        }
                                        clearable
                                        selection
                                        value={ideaCondition.field ?? undefined}
                                        options={subconditionOptions.ideaFields
                                          ?.filter(
                                            (f) =>
                                              !!f.type &&
                                              f.type in conditionChecks,
                                          )
                                          ?.map((f) => ({
                                            key: f.id,
                                            description: f.section,
                                            text: f.title,
                                            value: f.id,
                                          }))}
                                        onChange={(_e, { value }) => {
                                          const fieldId =
                                            dropdownStringValue(value);
                                          const fieldType =
                                            subconditionOptions.ideaFields?.find(
                                              (f) => f.id === fieldId,
                                            )?.type;
                                          setAnnouncement((prevState) => ({
                                            ...prevState,
                                            sendWhenConditions: {
                                              ...(prevState.sendWhenConditions ||
                                                {}),
                                              conditions:
                                                prevState.sendWhenConditions?.conditions?.map(
                                                  (c, j) => {
                                                    if (i === j) {
                                                      const defaultCondition =
                                                        isKnownFieldType(
                                                          fieldType,
                                                        )
                                                          ? getDefaultCondition(
                                                              fieldType,
                                                            )
                                                          : "isNotEmpty";
                                                      return {
                                                        field: fieldId ?? null,
                                                        condition:
                                                          defaultCondition ??
                                                          "isNotEmpty",
                                                        value: null,
                                                      } satisfies AnnouncementIdeaFieldCondition;
                                                    }
                                                    return c;
                                                  },
                                                ),
                                            },
                                          }));
                                        }}
                                      />
                                    </Form.Field>
                                    <Form.Field>
                                      {i === 0 ? (
                                        <label htmlFor={`condition-${i}`}>
                                          2. Meets condition
                                        </label>
                                      ) : null}
                                      <Form.Dropdown
                                        placeholder="Select a condition"
                                        selection
                                        value={ideaCondition.condition}
                                        options={
                                          isKnownFieldType(chosenFieldType)
                                            ? availableConditions[
                                                chosenFieldType
                                              ]
                                            : undefined
                                        }
                                        onChange={(_e, { value }) => {
                                          const nextCondition =
                                            dropdownStringValue(value);
                                          setAnnouncement((prevState) => ({
                                            ...prevState,
                                            sendWhenConditions: {
                                              ...(prevState.sendWhenConditions ||
                                                {}),
                                              conditions:
                                                prevState.sendWhenConditions?.conditions?.map(
                                                  (c, j) => {
                                                    if (i === j) {
                                                      return {
                                                        ...(c as AnnouncementIdeaFieldCondition),
                                                        condition:
                                                          nextCondition as AnnouncementIdeaFieldCondition["condition"],
                                                        value: null,
                                                      };
                                                    }
                                                    return c;
                                                  },
                                                ) as AnnouncementSendWhenCondition[],
                                            },
                                          }));
                                        }}
                                      />
                                    </Form.Field>
                                    <Form.Field>
                                      {i === 0 ? (
                                        <label htmlFor={`condition-${i}`}>
                                          2. Meets condition
                                        </label>
                                      ) : null}
                                      {ideaCondition.field &&
                                      ideaCondition.condition
                                        ? renderValueCheck(
                                            {
                                              field: ideaCondition.field,
                                              condition:
                                                ideaCondition.condition,
                                              value:
                                                ideaCondition.value ??
                                                undefined,
                                            },
                                            selectedIdeaField,
                                            (value) => {
                                              setAnnouncement((prevState) => ({
                                                ...prevState,
                                                sendWhenConditions: {
                                                  ...(prevState.sendWhenConditions ||
                                                    {}),
                                                  conditions:
                                                    prevState.sendWhenConditions?.conditions?.map(
                                                      (c, j) => {
                                                        if (i === j) {
                                                          return {
                                                            ...(c as AnnouncementIdeaFieldCondition),
                                                            value,
                                                          };
                                                        }
                                                        return c;
                                                      },
                                                    ) as AnnouncementSendWhenCondition[],
                                                },
                                              }));
                                            },
                                          )
                                        : null}
                                    </Form.Field>
                                    <Button
                                      style={{
                                        marginTop: i === 0 ? 25 : 0,
                                        maxHeight: "fit-content",
                                      }}
                                      icon="trash"
                                      onClick={() =>
                                        setAnnouncement((prevState) => ({
                                          ...prevState,
                                          sendWhenConditions: {
                                            ...(prevState.sendWhenConditions ||
                                              {}),
                                            conditions:
                                              prevState.sendWhenConditions?.conditions?.filter(
                                                (_c, j) => j !== i,
                                              ),
                                          },
                                        }))
                                      }
                                    />
                                  </Form.Group>
                                </>
                              );
                            },
                          )}
                          <Form.Group>
                            <Form.Button
                              onClick={() => {
                                setAnnouncement((prevState) => ({
                                  ...prevState,
                                  sendWhenConditions: {
                                    ...(prevState.sendWhenConditions || {}),
                                    conditions: [
                                      ...(prevState.sendWhenConditions
                                        ?.conditions ?? []),
                                      {
                                        field: null,
                                        condition: "isNotEmpty",
                                        value: null,
                                      } satisfies AnnouncementIdeaFieldCondition,
                                    ],
                                  },
                                }));
                              }}
                            >
                              Add a condition
                            </Form.Button>
                          </Form.Group>
                        </>
                      ) : null}
                      {announcement.sendWhen === "moveToLane" ? (
                        <>
                          <p>Select a lane</p>
                          <Select
                            value={(() => {
                              const firstCondition =
                                announcement.sendWhenConditions
                                  ?.conditions?.[0];
                              if (!firstCondition) return undefined;
                              if ("laneId" in firstCondition) {
                                return (
                                  firstCondition.laneId ??
                                  firstCondition.value ??
                                  undefined
                                );
                              }
                              if ("value" in firstCondition) {
                                return firstCondition.value ?? undefined;
                              }
                              return undefined;
                            })()}
                            onChange={(_e, { value }) => {
                              const laneId = dropdownStringValue(value);
                              setAnnouncement((prevState) => ({
                                ...prevState,
                                sendWhenConditions: {
                                  ...(prevState.sendWhenConditions || {}),
                                  conditions: [{ laneId, value: laneId }],
                                },
                              }));
                            }}
                            options={mapSubconditionOptions(
                              subconditionOptions.moveToLane,
                            )}
                            placeholder="Select a lane"
                          />
                        </>
                      ) : null}
                      {announcement.sendWhen ===
                      "submittedAllAssessmentsForChallenge" ? (
                        <Select
                          value={(() => {
                            const firstCondition =
                              announcement.sendWhenConditions?.conditions?.[0];
                            if (
                              firstCondition &&
                              "assessmentRoundId" in firstCondition
                            ) {
                              return (
                                firstCondition.assessmentRoundId ?? "legacy"
                              );
                            }
                            return "legacy";
                          })()}
                          onChange={(_e, { value }) => {
                            const assessmentRoundId =
                              dropdownStringValue(value) ?? "legacy";
                            setAnnouncement((prevState) => ({
                              ...prevState,
                              sendWhenConditions: {
                                ...(prevState.sendWhenConditions || {}),
                                conditions: [{ assessmentRoundId }],
                              },
                            }));
                          }}
                          options={mapSubconditionOptions(
                            subconditionOptions.assessmentRounds,
                          )}
                          placeholder="Select an assessment"
                        />
                      ) : null}
                    </Form>
                  </div>
                )}
              </div>
            )}
        </Segment>
        {announcement.countSent ? (
          <p>
            This announcement has been sent {announcement.countSent || 0} times.
          </p>
        ) : null}
        <AnnouncementBase
          forType={forType}
          forId={forId}
          announcement={announcement}
          setAnnouncement={setAnnouncement}
        />
        {announcement.type !== "automatic" && (
          <Message
            icon="info"
            info
            size="small"
            header={`Don't use ${util.appName()} to send out marketing messages`}
            content={
              "For GDPR purposes, announcements should be used only for platform activity and not for advertising events, services or other marketing correspondence."
            }
          />
        )}
      </Modal.Content>

      <Modal.Actions>
        {announcement.type !== "automatic" && (
          <span>
            {t("announcements.form.info", { audience: recipientsMatched })}
          </span>
        )}
        <Button
          style={{ marginLeft: 16 }}
          content={t("generic.cancel")}
          onClick={() => setAnnouncement(EMPTY_ANNOUNCEMENT)}
        />
        {announcement._id ? (
          <TooltipButton
            secondary
            icon="save"
            content={t("generic.saveChanges")}
            onClick={updateAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
          />
        ) : announcement.type !== "automatic" && announcement.sendAt ? (
          <TooltipButton
            secondary
            icon="calendar"
            content={
              sendAtValid
                ? `Send on ${formatDateAt(announcement.sendAt)}`
                : "Invalid date entered"
            }
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            tooltip={sendDisabledReason}
          />
        ) : (
          <TooltipButton
            secondary
            icon={announcement.type === "standard" ? "send" : "plus"}
            content={
              announcement.type === "standard"
                ? t("announcements.form.send")
                : "Create announcement"
            }
            onClick={createAnnouncement}
            loading={sending}
            disabled={sendDisabled}
            style={{ marginLeft: "0" }}
            tooltip={sendDisabledReason}
          />
        )}
      </Modal.Actions>
      <Modal.Actions>
        <TooltipButton
          content="Preview email"
          tooltip={sendDisabledReason}
          basic
          size="tiny"
          icon="eye"
          onClick={() => setEmailPreviewOpen(true)}
          disabled={sendDisabled}
        />
        {!announcement._id && (
          <Button
            icon="trash"
            size="tiny"
            content={t("generic.delete")}
            basic
            onClick={() => {
              setAnnouncement(EMPTY_ANNOUNCEMENT);
              removeLocalStorageItem(`announcement-${forId}`);
            }}
          />
        )}
        {announcement._id ? (
          <>
            {copyToNew ? (
              <Button
                basic
                size="tiny"
                icon="copy"
                content="Copy to new"
                onClick={() => {
                  setAnnouncement(EMPTY_ANNOUNCEMENT);
                  window.scrollTo(0, 0);
                  setTimeout(() => {
                    copyToNew(announcement);
                  }, 300);
                }}
              />
            ) : null}
            <Button
              onClick={() => {
                deleteAnnouncement(announcement._id ?? "", () => {
                  setAnnouncement(EMPTY_ANNOUNCEMENT);
                });
              }}
              content={t("generic.delete")}
              icon="trash"
              basic
              size="small"
            />
          </>
        ) : null}
      </Modal.Actions>
    </Modal>
  );
};

export default AnnouncementModal;
